// Keycloak-Singleton + Helpers. Initialisierung erfolgt in main.tsx vor
// dem React-Render, damit die App ab dem ersten Frame einen gültigen Token
// hat (oder im Login-Redirect ist).
//
// Pattern wie bei studio.macoapp.de: keycloak-js + login-required +
// silent-check-sso. Konfig kommt zur Build-Zeit aus VITE_KC_* Env-Vars
// (siehe deploy/k8s/configmap.yaml für die Defaults).

import Keycloak from "keycloak-js";

const KC_URL = (import.meta.env.VITE_KEYCLOAK_URL as string | undefined) ?? "https://keycloak.lief.maco.conuti.dev/auth";
const KC_REALM = (import.meta.env.VITE_KEYCLOAK_REALM as string | undefined) ?? "backoffice";
const KC_CLIENT = (import.meta.env.VITE_KEYCLOAK_CLIENT as string | undefined) ?? "backoffice-web";
// "true" oder "1" deaktiviert den Login -- nur für lokale Dev (npm run dev
// gegen localhost-Backend mit AUTH_DISABLED=true).
const AUTH_DISABLED = (import.meta.env.VITE_AUTH_DISABLED as string | undefined) === "true";

export const keycloak = AUTH_DISABLED
  ? null
  : new Keycloak({ url: KC_URL, realm: KC_REALM, clientId: KC_CLIENT });

let _authReady: Promise<boolean> | null = null;

/**
 * Startet den Keycloak-Login-Flow und gibt true zurück, sobald die Session
 * etabliert ist. Idempotent -- mehrfache Calls liefern dieselbe Promise.
 */
export function ensureAuth(): Promise<boolean> {
  if (AUTH_DISABLED || !keycloak) return Promise.resolve(true);
  if (_authReady) return _authReady;
  _authReady = keycloak
    .init({
      onLoad: "login-required",
      checkLoginIframe: false,         // iframes blockieren in vielen Browsern, silent-check ist robuster
      silentCheckSsoRedirectUri: window.location.origin + "/silent-check-sso.html",
      pkceMethod: "S256",
    })
    .then((authenticated) => {
      if (!authenticated) {
        // Wenn login-required failt, leitet keycloak-js automatisch auf /auth/realms/.../login um
        // -- wir landen hier nur wenn der Browser tab schon abgemeldet war.
        keycloak.login();
        return false;
      }
      // Token-Refresh-Loop: 60s vor Ablauf erneuern. Wenn fehlschlägt -> re-login.
      setInterval(() => {
        keycloak.updateToken(70).catch(() => keycloak.login());
      }, 30_000);
      return true;
    });
  return _authReady;
}

/** Bearer-Token für API-Calls. Leer bei AUTH_DISABLED. */
export function getToken(): string {
  if (AUTH_DISABLED || !keycloak) return "";
  return keycloak.token ?? "";
}

/** User-Info aus dem ID-Token (preferred_username/email). */
export function getUser(): { name: string; email: string } | null {
  if (AUTH_DISABLED) return { name: "dev", email: "dev@localhost" };
  const profile = keycloak?.tokenParsed as Record<string, unknown> | undefined;
  if (!profile) return null;
  return {
    name: (profile.preferred_username as string) || (profile.name as string) || "",
    email: (profile.email as string) || "",
  };
}

export function logout(): void {
  if (AUTH_DISABLED || !keycloak) return;
  keycloak.logout({ redirectUri: window.location.origin });
}

/** Unix-seconds Ablaufzeit des aktuellen Access-Tokens. `null` wenn unbekannt. */
export function getTokenExpiry(): number | null {
  if (AUTH_DISABLED) return null;
  const parsed = keycloak?.tokenParsed as { exp?: number } | undefined;
  return parsed?.exp ?? null;
}

/** Link zum Self-Service-Profil im Keycloak Account-Console. */
export function getAccountUrl(): string | null {
  if (AUTH_DISABLED || !keycloak) return null;
  try {
    return keycloak.createAccountUrl();
  } catch {
    return `${KC_URL.replace(/\/+$/, "")}/realms/${KC_REALM}/account/`;
  }
}
